import React, {
	ChangeEvent,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	SypacButton,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Close from '../../assets/Close';
import { Checkmark } from '../../assets/Checkmark';
import LoadingSpinner from '../../assets/LoadingSpinner';
import AlertSmall from '../../assets/AlertSmall';
import PdfIcon from '../../assets/PdfIcon';
import { OrderContext } from '../../context/OrderContext/order.context';
import { FileService } from '../../services/file.service';
import { OrdersService } from '../../services/orders.services';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';
import { FileInterface } from '../CompanyVerification/components/CompanyDocuments/CompanyDocuments.interface';
import StatusBadge from '../StatusBadge/StatusBadge';
import TruckIcon from '../TruckIcon/TruckIcon';
import { DriverActionFlow } from '../../constants/order-status.constants';
import { ModalDriverActionProps } from './ModalDriverAction.interface';
import {
	getWeightDifferencePercent,
	isMissingRequiredDocuments,
	WEIGHT_DIFFERENCE_TOLERANCE_PERCENT,
} from './driverAction.utils';

const ModalDriverAction: React.FC<ModalDriverActionProps> = ({
	isOpen,
	onClose,
	orderId,
	truck,
	deliveryIncluded,
	onSuccess,
	target = OrderTarget.admin,
}) => {
	const { t } = useTranslate();
	const { setAdditional } = useContext(OrderContext);
	const modalRef = useRef<HTMLDivElement>(null);

	const [localFiles, setLocalFiles] = useState<FileInterface[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [requestError, setRequestError] = useState<boolean>(false);
	const [documentsError, setDocumentsError] = useState<boolean>(false);
	const [confirmWeight, setConfirmWeight] = useState<boolean>(false);

	const currentIndex = useMemo(
		() => DriverActionFlow.findIndex((s) => s.status === truck.orderStatus),
		[truck.orderStatus],
	);
	const nextStep = currentIndex >= 0 ? DriverActionFlow[currentIndex] : null;
	// Unloaded weight can't differ from what was loaded, so it isn't asked for
	const loadedWeight = truck.loadDetails?.wight ?? truck.loadedQuantity ?? 0;

	const formik = useFormik({
		initialValues: {
			weightNumber: '',
			comments: '',
		},
		enableReinitialize: true,
		validationSchema: Yup.object({
			weightNumber: nextStep?.requiresWeight
				? Yup.string()
						.required(
							t(
								'modalLoadDetails.loadedWeightRequired',
								'Loaded weight is required.',
							),
						)
						.test(
							'not-zero',
							t(
								'modalLoadDetails.loadedWeightMustBeGreaterThanZero',
								'Loaded weight must be greater than 0.',
							),
							(value) => {
								if (!value) return true;
								const num = Number(value);
								return Number.isFinite(num) && num > 0;
							},
						)
				: Yup.string(),
		}),
		onSubmit: async (values) => {
			if (!nextStep) return;
			if (isWeightDifferent && !confirmWeight) {
				setConfirmWeight(true);
				return;
			}
			if (isMissingRequiredDocuments(nextStep, localFiles.length)) {
				setDocumentsError(true);
				return;
			}
			setIsLoading(true);
			setRequestError(false);
			try {
				let documents: string[] = [];
				if (localFiles.length) {
					const resp = await FileService.uploadFiles(
						localFiles.map((item) => item.file),
					);
					documents = (resp.data?.items ?? []).map((item) => item.name);
				}

				if (isMissingRequiredDocuments(nextStep, documents.length)) {
					setRequestError(true);
					return;
				}
				await OrdersService.doDriverAction(
					orderId,
					nextStep.action,
					{
						wight: nextStep.requiresWeight
							? +values.weightNumber
							: loadedWeight,
						documents,
						comments: values.comments || undefined,
					},
					target,
					truck.id,
				);
				onSuccess();
				handleModalClose();
			} catch (e) {
				setRequestError(true);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const weightDifferencePercent = nextStep?.requiresWeight
		? getWeightDifferencePercent(+formik.values.weightNumber, truck.payload)
		: null;
	const isWeightDifferent =
		weightDifferencePercent !== null &&
		Math.abs(weightDifferencePercent) > WEIGHT_DIFFERENCE_TOLERANCE_PERCENT;

	const handleModalClose = () => {
		formik.resetForm();
		setLocalFiles([]);
		setRequestError(false);
		setDocumentsError(false);
		setConfirmWeight(false);
		onClose();
	};

	const handleWeightChange = (e: ChangeEvent<HTMLInputElement>) => {
		formik.setFieldValue('weightNumber', e.target.value.replace(',', '.'));
	};

	const handleFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		if (!e.target.files?.length) return;
		const dataFiles: FileInterface[] = [];
		for (let file of e.target.files) {
			let mimeType = file.name.split('.').pop()?.toUpperCase() || '';
			if (file.type) {
				mimeType = file.type.split('/').pop()?.toUpperCase() || mimeType;
			}
			dataFiles.push({
				name: file.name,
				mimeType,
				size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
				file,
			});
		}
		setLocalFiles((prev) => [...prev, ...dataFiles]);
		setDocumentsError(false);
		e.target.value = '';
	};

	const handleRemoveFile = (index: number) => {
		setLocalFiles((prev) => prev.filter((_, i) => i !== index));
	};

	useEffect(() => {
		setAdditional((prev) => ({
			...prev,
			orderModalRef: modalRef,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return isOpen ? (
		<div className="modal-overlay" onClick={handleModalClose}>
			<div
				className="absolute bg-white outline-none rounded-xl w-fit min-w-[600px] max-w-[calc(100vw-32px)] h-fit max-h-[calc(100vh-32px)] flex flex-col overflow-hidden"
				onClick={(e) => e.stopPropagation()}
				ref={modalRef}
			>
				<form
					className="flex flex-col min-h-0"
					onSubmit={(e) => {
						e.preventDefault();
						formik.handleSubmit();
					}}
				>
					<div className="flex items-center justify-between p-3">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-90 mt-[4px]">
								<T keyName="modalDriverAction.title">Change truck status</T>
							</p>
						</SypacText>
						<SypacButton variant="subTitle">
							<button
								type="button"
								className="flex justify-center items-center h-[32px] w-[32px] p-0 bg-white border border-solid border-gray-10 transition hover:border-gray-60 rounded-lg"
								onClick={handleModalClose}
							>
								<Close width="10" height="10" />
							</button>
						</SypacButton>
					</div>
					<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0" />

					<div className="w-0 min-w-full flex flex-col gap-3 p-3 min-h-0 overflow-y-auto sypac-scrollbar box-border">
						<div className="flex items-center gap-4 bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
							<span className="w-[76px] h-[46px] flex items-center justify-center bg-white border border-solid border-gray-10 rounded-10">
								<TruckIcon type={truck.truckType} />
							</span>
							<div className="flex flex-col gap-1">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-90">
										{truck.driver?.driverFullName || '-'}
									</p>
								</SypacText>
								<SypacText variant="body-regular-small">
									<p className="text-gray-40">
										{truck.driver?.licensePlates || '-'}
									</p>
								</SypacText>
							</div>
							<div className="ml-auto">
								<StatusBadge
									status={truck.orderStatus}
									isTruckStatus
									deliveryIncluded={deliveryIncluded}
								/>
							</div>
						</div>

						{nextStep ? (
							<>
								<div className="flex flex-col border border-solid border-gray-10 rounded-10 overflow-hidden">
									{DriverActionFlow.map((step, index) => {
										const isDone = index < currentIndex;
										const isNext = index === currentIndex;
										return (
											<div
												key={step.action}
												className={`flex items-center gap-3 px-3 py-2 ${
													index
														? 'border-0 border-solid border-t border-gray-10'
														: ''
												} ${isNext ? 'bg-primary-violet/10' : ''}`}
											>
												<span
													className={`flex items-center justify-center w-5 h-5 rounded-full border border-solid text-xs ${
														isDone
															? 'bg-primary-violet border-primary-violet text-white'
															: isNext
															? 'border-primary-violet text-primary-violet'
															: 'border-gray-20 text-gray-40'
													}`}
												>
													{isDone ? (
														<Checkmark width={10} height={10} />
													) : (
														index + 1
													)}
												</span>
												<SypacText variant="body-regular-medium">
													<p
														className={
															isNext
																? 'text-gray-90 font-medium'
																: isDone
																? 'text-gray-40 line-through'
																: 'text-gray-40'
														}
													>
														<T keyName={step.label}>{step.action}</T>
													</p>
												</SypacText>
												{isNext ? (
													<SypacText
														variant="overline-regular-large"
														className="ml-auto"
													>
														<p className="text-primary-violet">
															<T keyName="modalDriverAction.nextStep">
																Next step
															</T>
														</p>
													</SypacText>
												) : null}
											</div>
										);
									})}
								</div>

								<div className="flex flex-col gap-3 bg-alabaster border border-solid border-gray-10 rounded-10 p-3">
									{nextStep.requiresDetails ? (
										<>
											{nextStep.requiresWeight ? (
												<SypacInput
													className="flex gap-2"
													error={
														!!(
															formik.touched.weightNumber &&
															formik.errors.weightNumber
														)
													}
												>
													<SypacText
														variant="overline-normal-large"
														className="mb-1"
													>
														<p className="text-sm">
															<T keyName="modalLoadDetails.loadedWeightTons">
																Loaded weight (tons)
															</T>{' '}
															<span className="text-red">*</span>
														</p>
													</SypacText>
													<input
														type="string"
														name="weightNumber"
														className={`h-[42px] py-2.5 pl-3 border rounded-[8px] placeholder:text-gray-40 placeholder:text-base ${
															formik.errors.weightNumber &&
															formik.touched.weightNumber
																? 'border-red-orange'
																: ''
														}`}
														placeholder={t(
															'modalDriverAction.enterWeight',
															'Enter weight',
														)}
														onChange={handleWeightChange}
														onBlur={formik.handleBlur}
														value={formik.values.weightNumber}
														disabled={confirmWeight}
													/>
													{formik.touched.weightNumber &&
													formik.errors.weightNumber ? (
														<div className="flex items-center gap-2">
															<span className="flex scale-[1.1]">
																<AlertSmall
																	color="#F7A2B8"
																	secondColor="#F44A77"
																/>
															</span>
															<span className="input-error">
																{formik.errors.weightNumber}
															</span>
														</div>
													) : null}
												</SypacInput>
											) : null}

											{confirmWeight ? (
												<div className="flex flex-col gap-3">
													<div className="flex gap-3">
														<span className="flex scale-[1.1]">
															<AlertSmall
																color="#F7A2B8"
																secondColor="#F44A77"
															/>
														</span>
														<SypacText variant="body-regular-medium">
															<p className="text-sm text-red">
																<T keyName="modalLoadDetails.areYouSureToSubmitWeight">
																	Are you sure you want to submit this weight?
																</T>
															</p>
														</SypacText>
													</div>

													<div className="flex flex-col gap-5 pl-[30px]">
														<SypacText variant="body-regular-medium">
															<p className="text-sm text-gray-80">
																<T keyName="modalLoadDetails.pleaseReviewTheDifference">
																	The loaded quantity differs from the ordered
																	amount. Please review the difference:
																</T>
															</p>
														</SypacText>

														<div className="flex flex-col">
															<SypacText variant="body-regular-medium">
																<p className="text-sm text-gray-80">
																	1.{' '}
																	<T keyName="baseOrderDetails.orderedQuantity">
																		Ordered quantity:
																	</T>{' '}
																	{`${truck.payload} tons`}
																</p>
															</SypacText>
															<SypacText variant="body-regular-medium">
																<p className="text-sm text-gray-80">
																	2.{' '}
																	<T keyName="baseOrderDetails.loadedQuantity">
																		Loaded quantity:
																	</T>{' '}
																	{`${formik.values.weightNumber} tons`}
																</p>
															</SypacText>
															<SypacText variant="body-regular-medium">
																<p className="text-sm text-gray-80">
																	3.{' '}
																	<T keyName="modalLoadDetails.weightDifference">
																		Weight difference:
																	</T>{' '}
																	{`${weightDifferencePercent}%`}
																</p>
															</SypacText>
														</div>

														<SypacText variant="body-regular-medium">
															<p className="text-sm text-gray-80">
																<T keyName="modalLoadDetails.verifyAndConfirm">
																	Verify and confirm before proceeding.
																</T>
															</p>
														</SypacText>
													</div>
												</div>
											) : null}

											{localFiles.length ? (
												<div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-2">
													{localFiles.map((file, index) => (
														<div
															className="flex flex-row"
															key={`${file.name}-${index}`}
														>
															<div className="w-[56px] h-[56px] flex justify-center items-center bg-white border border-solid border-gray-10 rounded-lg">
																<PdfIcon />
															</div>
															<div className="flex flex-col ml-3 overflow-hidden">
																<SypacText variant="body-regular-small">
																	<p className="text-gray-80 truncate">
																		{file.name}
																	</p>
																</SypacText>
																<div className="flex flex-row items-center mt-1">
																	<SypacText variant="overline-regular-large">
																		<p className="text-gray-20">
																			{file.mimeType}
																		</p>
																	</SypacText>
																	<div className="bg-gray-20 w-1 h-1 rounded-full mx-2" />
																	<SypacText variant="overline-regular-large">
																		<p className="text-gray-20">{file.size}</p>
																	</SypacText>
																</div>
																<SypacText
																	variant="overline-normal-large"
																	className="mt-1 cursor-pointer"
																	onClick={() => handleRemoveFile(index)}
																>
																	<p className="text-red">
																		<T keyName="uploadDocuments.deleteFile">
																			Delete file
																		</T>
																	</p>
																</SypacText>
															</div>
														</div>
													))}
												</div>
											) : null}

											<input
												type="file"
												id="input-file-driver-action"
												className="hidden"
												multiple={true}
												accept="image/jpeg,image/gif,image/png,application/pdf,image/x-eps"
												onChange={handleFilesChange}
											/>
											<label
												htmlFor="input-file-driver-action"
												className="w-full box-border py-2.5 bg-white text-center rounded-lg border border-solid border-primary-violet transition hover:border-gray-20 cursor-pointer"
											>
												<SypacText variant="body-regular-medium">
													<p className="text-primary-violet">
														<T keyName="modalLoadDetails.uploadDocumentButton">
															Upload document
														</T>
													</p>
												</SypacText>
											</label>
											{documentsError ? (
												<div className="flex gap-3" role="alert">
													<span className="flex scale-[1.1]">
														<AlertSmall color="#F7A2B8" secondColor="#F44A77" />
													</span>
													<SypacText variant="body-regular-medium">
														<p className="text-sm text-red">
															<T keyName="modalDriverAction.documentsRequired">
																Please upload at least one photo or document
																before continuing.
															</T>
														</p>
													</SypacText>
												</div>
											) : null}
										</>
									) : null}

									<SypacInput className="w-full flex gap-2">
										<SypacText variant="overline-normal-large" className="mb-1">
											<p className="text-sm">
												<T keyName="modalLoadDetails.additionalInformation">
													Additional information
												</T>
											</p>
										</SypacText>
										<div className="relative w-full">
											<textarea
												name="comments"
												className="w-full box-border h-[98px] font-sans py-3.5 pl-3 border border-solid border-gray-20 hover:border-gray-40/75 focus:border-[#5682fa] rounded-xl placeholder:text-gray-40 placeholder:text-base resize-none outline-none"
												rows={3}
												maxLength={255}
												placeholder={t(
													'modalLoadDetails.writeAdditionalInformation',
													'Write additional information',
												)}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.comments}
											/>
											<div className="absolute bottom-[12px] right-[12px] flex border border-solid border-gray-10 text-xs text-gray-40 rounded-2xl px-1 py-[1px]">{`${formik.values.comments.length}/255`}</div>
										</div>
									</SypacInput>
								</div>
							</>
						) : (
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-center py-3">
									<T keyName="modalDriverAction.noActionsLeft">
										All steps are completed for this truck.
									</T>
								</p>
							</SypacText>
						)}

						{requestError ? (
							<div className="flex gap-3">
								<span className="flex scale-[1.1]">
									<AlertSmall color="#F7A2B8" secondColor="#F44A77" />
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-sm text-red">
										<T keyName="modalDriverAction.actionFailed">
											Failed to change the status. Please try again.
										</T>
									</p>
								</SypacText>
							</div>
						) : null}
					</div>

					<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0" />
					<div
						className={`${
							nextStep ? 'grid grid-cols-2' : 'flex'
						} w-full p-3 gap-4 box-border [&_button]:transition [&_button]:rounded-lg`}
					>
						<SypacButton variant="secondary" size="small" className="w-full">
							<button
								className="w-full py-2.5"
								type="button"
								onClick={
									confirmWeight
										? () => setConfirmWeight(false)
										: handleModalClose
								}
							>
								<SypacText variant="body-regular-medium">
									<p>
										{confirmWeight ? (
											<T keyName="modalLoadDetails.edit">Edit</T>
										) : (
											<T keyName="modalLoadDetails.cancel">Cancel</T>
										)}
									</p>
								</SypacText>
							</button>
						</SypacButton>
						{nextStep ? (
							<SypacButton variant="primary" size="small" className="w-full">
								<button
									className="w-full py-2.75"
									type="submit"
									disabled={
										isLoading ||
										(nextStep.requiresWeight &&
											(!formik.isValid || !formik.values.weightNumber)) ||
										isMissingRequiredDocuments(nextStep, localFiles.length)
									}
								>
									{isLoading ? <LoadingSpinner /> : <Checkmark />}
									<SypacText variant="body-regular-medium">
										<p className="ml-3 text-white whitespace-nowrap">
											<T keyName={nextStep.label}>{nextStep.action}</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>
						) : null}
					</div>
				</form>
			</div>
		</div>
	) : null;
};

export default ModalDriverAction;
